/**
 * Shared TanStack Query client.
 *
 * Server state in this app was previously fetched with ad-hoc `useEffect` +
 * `useState` in every component, which meant no shared cache, no request
 * deduplication, and a full refetch on every mount. This is the standard layer
 * for new data access; existing screens migrate to it incrementally.
 *
 * The defaults are chosen for a multi-tenant operations product:
 *   * Data is workspace-scoped, so every key must start with the organization id
 *     (see `orgQueryKey`) — otherwise switching workspace would serve another
 *     tenant's cached rows.
 *   * Operators keep tabs open for hours, so refetch on focus is on but the
 *     stale window is generous enough to avoid hammering the database.
 *   * A failed query retries once. Supabase errors are usually permission or
 *     validation problems that will not fix themselves, so retrying harder just
 *     delays the error the operator needs to see.
 */
import { QueryClient } from '@tanstack/react-query';

export const QUERY_STALE_TIME_MS = 30_000;
export const QUERY_GC_TIME_MS = 5 * 60_000;

export function createSaasQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: QUERY_STALE_TIME_MS,
        gcTime: QUERY_GC_TIME_MS,
        refetchOnWindowFocus: true,
        retry: 1,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

/**
 * Builds a cache key that cannot collide across workspaces.
 *
 * Passing the organization id explicitly rather than reading it from context
 * keeps the function pure and makes the tenant scope visible at every call site.
 */
export function orgQueryKey(
  organizationId: string | null | undefined,
  ...parts: ReadonlyArray<string | number | boolean | null | undefined>
): Array<string | number | boolean> {
  const scope = organizationId?.trim() || 'platform';
  return [
    'org',
    scope,
    ...parts.filter((part): part is string | number | boolean => part !== null && part !== undefined),
  ];
}

/**
 * Whether a query should run. Workspace-scoped queries must wait for the
 * organization id, or the first request fires against the wrong scope and
 * poisons the cache under a 'platform' key.
 */
export function isOrgQueryEnabled(organizationId: string | null | undefined, ...flags: boolean[]): boolean {
  return Boolean(organizationId?.trim()) && flags.every(Boolean);
}
